/**
 * PATH: apps/quote/src/pages/pricelist-page/ItemsTab.tsx
 * PURPOSE: Pricebook items table with filters and inline actions
 */

import { Search, Edit, Trash2, ExternalLink, History, FileSpreadsheet, Loader2 } from 'lucide-react';
import type { PriceBook, PriceBookItem } from '@/lib/quoteApi';
import { formatCurrency, cn } from '@/lib/utils';

interface ItemsTabProps {
  pricebooks: PriceBook[];
  activePricebookId: string | undefined;
  onSelectPricebook: (id: string) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  categoryFilter: string;
  onCategoryChange: (cat: string) => void;
  categories: string[];
  filteredItems: PriceBookItem[];
  items: PriceBookItem[];
  itemsLoading: boolean;
  onDeleteItem: (pricebookId: string, itemId: string) => void;
  onEditItem: (item: PriceBookItem) => void;
}

export default function ItemsTab({
  pricebooks,
  activePricebookId,
  onSelectPricebook,
  searchQuery,
  onSearchChange,
  categoryFilter,
  onCategoryChange,
  categories,
  filteredItems,
  items,
  itemsLoading,
  onDeleteItem,
  onEditItem,
}: ItemsTabProps) {
  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        {pricebooks.length > 1 && (
          <select
            value={activePricebookId || ''}
            onChange={(e) => onSelectPricebook(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            {pricebooks.map((pb) => (
              <option key={pb.id} value={pb.id}>
                {pb.name} {pb.is_default ? '(Default)' : ''}
              </option>
            ))}
          </select>
        )}

        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
        </div>

        <select
          value={categoryFilter}
          onChange={(e) => onCategoryChange(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="all">All Types</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {/* Items Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        {itemsLoading ? (
          <div className="p-8 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            {items.length === 0
              ? 'No items in this pricebook yet. Add items or sync from Xero.'
              : 'No items match your search.'}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Code</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Name</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Type</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Unit</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Cost</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Sell</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Markup</th>
                <th className="px-6 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">Source</th>
                <th className="px-6 py-3 w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredItems.slice(0, 100).map((item) => {
                const baseCost = typeof item.base_cost === 'string' ? parseFloat(item.base_cost) : item.base_cost;
                const sellPrice = item.computed_sell_price
                  ? (typeof item.computed_sell_price === 'string' ? parseFloat(item.computed_sell_price) : item.computed_sell_price)
                  : baseCost * (1 + (parseFloat(String(item.markup_percent)) || 0) / 100);
                const markup = typeof item.markup_percent === 'string' ? parseFloat(item.markup_percent) : item.markup_percent;

                return (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 font-medium text-gray-900 dark:text-white font-mono text-sm">{item.item_code}</td>
                    <td className="px-6 py-4">
                      <div className="text-gray-900 dark:text-white">{item.item_name}</div>
                      {item.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">{item.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={cn(
                        "px-2 py-1 rounded-full text-xs font-medium",
                        item.item_type === 'material' && "bg-green-100 text-green-800",
                        item.item_type === 'labour' && "bg-blue-100 text-blue-800",
                        item.item_type === 'plant' && "bg-orange-100 text-orange-800",
                        item.item_type === 'subcontractor' && "bg-purple-100 text-purple-800",
                        item.item_type === 'service' && "bg-cyan-100 text-cyan-800"
                      )}>
                        {item.item_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600 dark:text-gray-400">{item.unit}</td>
                    <td className="px-6 py-4 text-right text-gray-600 dark:text-gray-400">{formatCurrency(baseCost)}</td>
                    <td className="px-6 py-4 text-right font-medium text-gray-900 dark:text-white">{formatCurrency(sellPrice)}</td>
                    <td className="px-6 py-4 text-right text-green-600">{markup?.toFixed(1)}%</td>
                    <td className="px-6 py-4 text-center">
                      <SourceBadge source={item.source} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => onEditItem(item)}
                          title="Edit item"
                          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg"
                        >
                          <Edit className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        </button>
                        <button
                          onClick={() => {
                            if (activePricebookId && confirm('Delete this item?')) {
                              onDeleteItem(activePricebookId, item.id);
                            }
                          }}
                          className="p-2 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SourceBadge({ source }: { source?: string }) {
  if (source === 'xero') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-blue-600">
        <ExternalLink className="h-3 w-3" /> Xero
      </span>
    );
  }
  if (source === 'historical') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-purple-600">
        <History className="h-3 w-3" /> History
      </span>
    );
  }
  if (source === 'supplier_quote') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-amber-600">
        <FileSpreadsheet className="h-3 w-3" /> Supplier
      </span>
    );
  }
  return <span className="text-xs text-gray-500">Manual</span>;
}
